import React, { useEffect, useMemo, useState } from 'react'
import { Accordion, Button, Divider, Form, Modal } from 'semantic-ui-react'
import { useTextResources } from '../../language'
import PropTypes from "prop-types";
import TemplatesDropDown from '../shared/TemplatesDropdown';
import { onError, onSuccess, onWarning } from '../shared/messages';
import { GetSentEmailHistory, RenderTemplate, RenderTemplateToPdf, SendMail } from '../../api/services/pms';
import { useLoader } from '../../hooks/useLoader';
import SendEmailHistoryDropDown from '../shared/SendEmailHistoryDropDown';
import { HtmlEditor } from '../shared/HtmlEditor';

const SendMailPopup = ({ reservation: initialReservation, controller, ...rest }) => {
    const [to, setTo] = useState(initialReservation?.Guest?.Email || "")
    const [cc, setCc] = useState("")
    const [bcc, setBcc] = useState("")
    const [subject, setSubject] = useState("")
    const [body, setBody] = useState("")
    const [template, setTemplate] = useState()
    const [history, setHistory] = useState()
    const [isOpen, setIsOpen] = useState(rest.open)
    const [reservation, setReservation] = useState(initialReservation)
    const loader = useLoader()
    const text = useTextResources()

    useEffect(()=>{
        setSubject("")
        setBody("")
        setCc("")
        setBcc("")
        setTemplate(null)
        setHistory(null)
        setIsOpen(rest.open)
    }, [rest.open])

    useEffect(() => {
        if (controller) {
            window[controller] = (action, params) => {
                switch (action) {
                    case "open":
                        if (params) {
                            setTo(params.Reservation?.Guest?.Email ||  "")
                            setReservation(params.Reservation)
                            setIsOpen(true)
                        }
                        break;
                    default:
                        break;
                }
            }
        }
    }, [])

    useEffect(()=>{
        setReservation(initialReservation)
    }, [initialReservation])

    useEffect(()=>{
        setTo(reservation?.Guest?.Email || "")
    }, [reservation, isOpen])

    useEffect(() => {
        if (template) {
            setHistory(null);
            (async ()=>{
                try {
                    const rendered = await RenderTemplate({
                        Paramters: {
                            ReservationId: reservation?reservation.Id:null
                        },
                        TemplateID: template.Id
                    })
                    setBody(rendered.Body)
                    setSubject(rendered.Subject)
                } catch (error) {
                    onError(error)
                }
            })()
        }
    }, [template, reservation])

    useEffect(() => {
        if (history) {
            setTemplate(null);
            (async ()=>{
                try {
                    const items = await GetSentEmailHistory({
                        HotelID: reservation?.HotelId,
                        IDs: history.Id,
                        IncludeBody: true
                    })
                    if (items && items.length) {
                        setBody(items[0].Body)
                        setSubject(items[0].Subject)
                    }
                } catch (error) {
                    onError(error)
                }
            })()
        }
    }, [history, reservation])

    const templatesSpec = useMemo(() => {
        const ids = [1,11,12,13,14,2,21,22]
        const result = {
            HotelID: reservation?.HotelId,
            DeliveryTypeIDs: 1,
            IncludeTemplateBody: false,
            IncludeLanguage: true
        }
        ids.forEach((id, idx)=>{
            result[`TemplateTypeIDs[${idx}]`] = id
        })
        return result
    }, [reservation])

    const historySpec = useMemo(() => {
        return {
            EntityID: reservation?.Id,
            EntityTypeID: 1,
            HotelID: reservation?.HotelId,
            IncludeBody: false,
            IncludeSentEmailHistoryEntities: true,
            IncludeTemplate: true,
            IncludeTemplateBody: false,
            IncludeTemplateLanguage: true,
            IncludeTemplateType: true,
            IncludeUserName: true,
        }
    }, [reservation])

    const send = async ()=>{
        try {
            loader(true)
            const historyEntities = []
            if (reservation) {
                historyEntities.push({
                    EntityTypeID: 1,
                    EntityID: reservation.Id
                })
                if (reservation.Guest){
                    historyEntities.push({
                        EntityTypeID: 3,
                        EntityID: reservation.Guest.Id
                    })
                }
            }
            await SendMail({
                HotelId:  reservation.HotelId,
                To: [to],
                Cc: cc?[cc]:null,
                Bcc: bcc?[bcc]:null,
                Subject: subject,
                Body: body,
                SentEmailHistoryEntities:historyEntities
            })
            onSuccess("SentSuccessfully")
            setIsOpen(false)
        } catch (error) {
            error.status === 400?onWarning(error):onError(error);
        } finally {
            loader(false)
        }
    }
    const print = async ()=>{
        try {
            loader(true)

            const result = await RenderTemplateToPdf({
                FileName: template?template.TypeName:"" + " - " + reservation.Number,
                IsPrintable: true,
                TemplateID: template?template.Id:null,
                Paramters: [
                    {
                        key: "ReservationId",
                        value: reservation?reservation.Id: null
                    },
                    {
                        key: "HotelID",
                        value: reservation?reservation.HotelId: null
                    }
                ]
            })
            window.open(result.DmzUrlPathAccessibleByBrowser)
            setIsOpen(false)
        } catch (error) {
            onError(error)
        } finally {
            loader(false)
        }
    }

    const closeHandler = () =>{       
        rest?.onClose?.()
        setIsOpen(false)
    }

    return <Modal open={isOpen} {...rest} size="large">
        <Modal.Header>{text("SendEmail")}</Modal.Header>
        <Modal.Content style={{maxHeight: 690, overflowY: "auto"}}>
            <Form>
                <Form.Input required label={text("SendTo")} value={to} onChange={(e, { value }) => setTo(value)} />
                <Accordion as={Form.Field} panels={[
                    {
                        key: "details",
                        title: text("AddRecipients"),
                        content: {
                            content: <div>
                                <Form.Input label={text("Cc")} value={cc} onChange={(e, { value }) => setCc(value)} />
                                <Form.Input label={text("Bcc")} value={bcc} onChange={(e, { value }) => setBcc(value)} />
                            </div>
                        }
                    }
                ]} />
                <Form.Input  required label={text("Subject")} value={subject} onChange={(e, { value }) => setSubject(value)} />
                <Form.Group widths="2">
                    <Form.Field clearable label={text("Template")} control={TemplatesDropDown} value={template?template.Id:null} spec={templatesSpec} onChangeItem={setTemplate} />
                    <Form.Field clearable label={text("History")} control={SendEmailHistoryDropDown} value={history?history.Id:null} spec={historySpec} onChangeItem={setHistory} />
                </Form.Group>

                <Divider />
                <HtmlEditor value={body} onChange={setBody} />
            </Form>
        </Modal.Content>
        <Modal.Actions>
            <Button negative size="small" onClick={closeHandler}>{text("Cancel")}</Button>
            <Button disabled={!body} size="small" onClick={print}>{text("Print")}</Button>
            <Button disabled={!to || !subject || !body} positive size="small" onClick={send}>{text("Send")}</Button>
        </Modal.Actions>
    </Modal>
}
SendMailPopup.propTypes = {
    reservation: PropTypes.object,
    controller: PropTypes.string
}
export default SendMailPopup
